import { Color as PersonaColor, Spacing } from '@persona-id/design-tokens';
import jsonLogic from 'json-logic-js';
import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { v4 as uuid } from 'uuid';

import EditOrRulesBlock from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/ComponentEditorSections/ComponentJsonLogicEditor/blocks/EditOrRulesBlock';
import EditRule from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/ComponentEditorSections/ComponentJsonLogicEditor/blocks/EditRule';
import { ConditionalRule, LogicBlockOrV2 } from 'dashboard/lib/workflows';
import { WorkflowOperatorKey } from 'dashboard/models/workflow';
import Spacer from 'design-system/components/Spacer';
import { FieldSchemaType } from 'lib/fields';
import { JsonSchema } from 'lib/json-schema';

const RulesBlock = styled.div`
  background: ${PersonaColor.BackgroundPrimary};
  margin-bottom: ${Spacing[2]};
  padding: ${Spacing[2]} ${Spacing[2]};
  border-radius: ${Spacing[1]};
`;

const RuleLine = styled.div`
  display: grid;
  grid-template-columns: ${Spacing[8]} minmax(0, 1fr);
`;

const PrefixText = styled.p`
  margin-top: ${Spacing[2]};
`;

interface EditAndRulesBlockProps {
  andRules: ConditionalRule[];
  jsonSchema: JsonSchema;
  operatorsMap: Partial<Record<FieldSchemaType, WorkflowOperatorKey[]>>;
  orPrefixText: string;
  orIndex: number;
  editable: boolean;
  enableValidation: boolean;
  addAndRule: (path: string[]) => void;
  addOrRule: (path: string[]) => void;
  deleteRule: (orPath: string[], andIndex: number) => void;
  updateRule: (path: string[]) => (rule: ConditionalRule) => void;
  validateRule: (rule: ConditionalRule) => boolean;
}

const EditAndRulesBlock = ({
  andRules,
  editable,
  orPrefixText,
  jsonSchema,
  operatorsMap,
  orIndex,
  enableValidation,
  addAndRule,
  addOrRule,
  updateRule,
  deleteRule,
  validateRule,
}: EditAndRulesBlockProps) => {
  const [keys, setKeys] = useState(andRules.map(() => uuid()));
  const [internalAndRules, setAndRules] = useState(andRules);
  const [ruleValidityMap, setRuleValidityMap] = useState(
    andRules.reduce(
      (map, baseRule, index) => {
        return { ...map, [index]: validateRule(baseRule) };
      },
      {} as Record<number, boolean>,
    ),
  );

  // Keep track of keys for each edit rule line so that if a
  // rule is added or deleted, the react state is not associated
  // with the wrong rule
  useEffect(() => {
    const newKeys = keys.slice();
    internalAndRules.forEach((_, index) => {
      if (!newKeys[index]) {
        newKeys.push(uuid());
      }
    });
    setKeys(newKeys);
  }, [internalAndRules.length]);

  useEffect(() => {
    setAndRules(andRules);
    const newValidityMap = andRules.reduce(
      (map, baseRule, index) => {
        return { ...map, [index]: validateRule(baseRule) };
      },
      {} as Record<number, boolean>,
    );
    setRuleValidityMap(newValidityMap);
  }, [andRules]);

  return (
    <>
      {andRules.map((baseRule, andIndex) => {
        const operator = jsonLogic.get_operator(baseRule);
        const nestedRuleAddPath = [`${andIndex}`, 'or'];
        const nestedRuleUpdatePath = [`${orIndex}`, 'and', `${andIndex}`];
        if (operator === 'or') {
          return (
            <div key={`component-conditional__${andIndex}__OrRules`}>
              <Spacer height={2} />
              <RuleLine>
                <p>{andIndex === 0 ? orPrefixText : 'And'}</p>
                <RulesBlock>
                  <EditOrRulesBlock
                    keyPrefix='component-conditional__'
                    prefixText='When'
                    nestedLayer='inner'
                    rules={baseRule as unknown as LogicBlockOrV2}
                    jsonSchema={jsonSchema}
                    operatorsMap={operatorsMap}
                    editable={editable}
                    validateRule={validateRule}
                    updateRule={(path) => updateRule([...nestedRuleUpdatePath, 'or', ...path])}
                    deleteRule={(path, nestedAndIndex) =>
                      deleteRule([...nestedRuleUpdatePath, 'or', ...path], nestedAndIndex)
                    }
                    addAndRule={(path) => addAndRule([...nestedRuleAddPath, ...path])}
                    addOrRule={() => addOrRule(nestedRuleAddPath)}
                    enableValidation
                  />
                </RulesBlock>
              </RuleLine>
            </div>
          );
        } else {
          return (
            <RuleLine key={keys[andIndex]}>
              <PrefixText>{andIndex === 0 ? orPrefixText : 'And'}</PrefixText>
              <EditRule
                baseRule={baseRule}
                jsonSchema={jsonSchema}
                updateRule={updateRule(nestedRuleUpdatePath)}
                deleteRule={() => {
                  deleteRule([`${orIndex}`, 'and'], andIndex);
                  setKeys([...keys.slice(0, andIndex), ...keys.slice(andIndex + 1)]);
                  setAndRules([
                    ...internalAndRules.slice(0, andIndex),
                    ...internalAndRules.slice(andIndex + 1),
                  ]);
                }}
                operatorsMap={operatorsMap}
                editable={editable}
                enableValidation={enableValidation}
                isValid={ruleValidityMap[andIndex]}
              />
            </RuleLine>
          );
        }
      })}
    </>
  );
};

export default EditAndRulesBlock;
